/* eslint-disable @typescript-eslint/typedef */
import { createSlice } from '@reduxjs/toolkit';

import { Team, TeamMember } from '../../../types/entity.types';
import {
  AddMemberToTeamPayload,
  AddTeamPayload,
  ChangeTeamNamePayload,
  RemoveMemberFromTeamPayload,
  RemoveTeamPayload,
  SetTeamPayload,
  SetTeamsPayload,
  TeamState
} from '../../../types/state/team.types';

export const initialState: TeamState = {
  team: undefined,
  teams: []
};

const TeamSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {
    setTeam: (state, action: SetTeamPayload): void => {
      state.team = action.payload;
    },
    setTeams: (state, action: SetTeamsPayload): void => {
      state.teams = action.payload;
    },
    addTeam: (state, action: AddTeamPayload): void => {
      state.teams.push(action.payload);
    },
    changeTeamName: (state, action: ChangeTeamNamePayload): void => {
      const { externalReference, teamName } = action.payload;

      if (!state.team) throw new Error('Could not change team name.');

      state.team.name = teamName;

      const foundTeam: Team | undefined = state.teams.find(
        (team: Team): boolean => team.externalReference === externalReference
      );

      if (!foundTeam) throw new Error('Could not change team name.');

      foundTeam.teamName = teamName;
    },
    removeTeam: (state, action: RemoveTeamPayload): void => {
      state.teams = state.teams.filter((team: Team): boolean => team.externalReference !== action.payload);
    },
    addMemberToTeam: (state, action: AddMemberToTeamPayload): void => {
      const { teamExternalReference, member } = action.payload;

      if (!state.team?.teamMembers) throw new Error('Could not add member to team.');

      state.team.teamMembers.push(member);

      const foundTeam: Team | undefined = state.teams.find(
        (team: Team): boolean => team.externalReference === teamExternalReference
      );

      if (!foundTeam) throw new Error('Could not add member to team.');

      if (!foundTeam.numberOfMembers && foundTeam.numberOfMembers !== 0) {
        foundTeam.numberOfMembers = 1;
      } else {
        foundTeam.numberOfMembers++;
      }
    },
    removeMemberFromTeam: (state, action: RemoveMemberFromTeamPayload): void => {
      const { teamExternalReference, memberExternalReference } = action.payload;

      if (!state.team?.teamMembers) throw new Error('Could not remove member from team.');

      state.team.teamMembers = state.team.teamMembers.filter(
        (member: TeamMember): boolean => member.memberExternalReference !== memberExternalReference
      );

      const foundTeam: Team | undefined = state.teams.find(
        (team: Team): boolean => team.externalReference === teamExternalReference
      );

      if (!foundTeam?.numberOfMembers) throw new Error('Could not remove member from team.');

      foundTeam.numberOfMembers--;
    }
  }
});

export const { setTeam, setTeams, addTeam, removeTeam, changeTeamName, addMemberToTeam, removeMemberFromTeam } =
  TeamSlice.actions;

export default TeamSlice.reducer;
